import { apiClient } from './apiClient';

// Simuliere API-Verzögerung
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class GroupsService {
  constructor() {
    this.groups = [];
  }

  // Alle Gruppen abrufen
  async getAllGroups() {
    try {
      await delay(300); // Simuliere API-Verzögerung
      
      const response = await apiClient.get('/groups');
      
      // apiClient gibt direkt data zurück, nicht das gesamte Response-Objekt
      // Stelle sicher, dass wir immer ein Array zurückgeben (auch wenn leer)
      if (response !== null && response !== undefined) {
        const groupsData = Array.isArray(response) ? response : [];
        return {
          success: true,
          data: groupsData,
          message: 'Gruppen erfolgreich geladen'
        };
      } else {
        // Wenn response null/undefined ist, geben wir ein leeres Array zurück
        return {
          success: true,
          data: [],
          message: 'Keine Gruppen vorhanden'
        };
      }
    } catch (error) {
      console.error('Fehler beim Laden der Gruppen:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Laden der Gruppen',
        details: error.message
      };
    }
  }

  // Gruppe nach ID abrufen
  async getGroupById(id) {
    try {
      await delay(200);
      
      const response = await apiClient.get(`/groups/${id}`);
      
      if (response) {
        return {
          success: true,
          data: response,
          message: 'Gruppe erfolgreich geladen'
        };
      } else {
        return {
          success: false,
          error: 'Gruppe nicht gefunden'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Laden der Gruppe',
        details: error.message
      };
    }
  }

  // Gruppe mit Startreihenfolge abrufen
  async getGroupWithStartOrder(groupId) {
    try {
      await delay(300);
      
      const response = await apiClient.get(`/groups/${groupId}/start-order`);
      
      if (response) {
        return {
          success: true,
          data: response,
          message: 'Gruppe mit Startreihenfolge erfolgreich geladen'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Laden der Startreihenfolge'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Laden der Startreihenfolge',
        details: error.message
      };
    }
  }

  // Startreihenfolge aktualisieren
  async updateStartOrder(groupId, startOrder) {
    try {
      await delay(500);
      
      const response = await apiClient.put(`/groups/${groupId}/start-order`, { startOrder });
      
      if (response) {
        return {
          success: true,
          message: 'Startreihenfolge erfolgreich aktualisiert'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Aktualisieren der Startreihenfolge'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Aktualisieren der Startreihenfolge',
        details: error.message
      };
    }
  }

  // Teilnehmer zu Gruppe hinzufügen
  async addParticipantToGroup(groupId, participantId, startNumber = null) {
    try {
      await delay(300);
      
      const response = await apiClient.post('/groups/participants', { groupId, participantId, startNumber });
      
      if (response) {
        return {
          success: true,
          data: response,
          message: 'Teilnehmer erfolgreich zur Gruppe hinzugefügt'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Hinzufügen des Teilnehmers'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Hinzufügen des Teilnehmers',
        details: error.message
      };
    }
  }

  // Teilnehmer aus Gruppe entfernen
  async removeParticipantFromGroup(groupId, participantId) {
    try {
      await delay(300);
      
      const response = await apiClient.delete(`/groups/${groupId}/participants/${participantId}`);
      
      if (response) {
        return {
          success: true,
          message: 'Teilnehmer erfolgreich aus der Gruppe entfernt'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Entfernen des Teilnehmers'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Entfernen des Teilnehmers',
        details: error.message
      };
    }
  }

  // Gruppen nach Kriterien filtern
  async getGroupsByFilter(filters = {}) {
    try {
      await delay(300);

      // Query-Parameter aufbauen
      const queryParams = new URLSearchParams();
      
      if (filters.categoryId && filters.categoryId !== 'all') {
        queryParams.append('categoryId', filters.categoryId);
      }
      
      if (filters.ageGroupId && filters.ageGroupId !== 'all') {
        queryParams.append('ageGroupId', filters.ageGroupId);
      }
      
      if (filters.gender && filters.gender !== 'all') {
        queryParams.append('gender', filters.gender);
      }
      
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }

      const endpoint = `/groups${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(endpoint);
      
      if (response) {
        // Client-seitige Filterung für Suchbegriffe
        let filteredGroups = response;
        
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filteredGroups = filteredGroups.filter(g => 
            g.name.toLowerCase().includes(searchLower) ||
            (g.description && g.description.toLowerCase().includes(searchLower)) ||
            (g.category && g.category.name && g.category.name.toLowerCase().includes(searchLower))
          );
        }

        return {
          success: true,
          data: filteredGroups,
          message: `${filteredGroups.length} Gruppen gefunden`
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Filtern der Gruppen'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Filtern der Gruppen',
        details: error.message
      };
    }
  }

  // Gruppen neu generieren (nur für Admins)
  async refreshGroups() {
    try {
      await delay(1000); // Längere Verzögerung für Refresh-Operation
      
      const response = await apiClient.post('/groups/refresh');
      
      if (response) {
        return {
          success: true,
          message: 'Gruppen erfolgreich neu generiert'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Neu generieren der Gruppen'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Neu generieren der Gruppen',
        details: error.message
      };
    }
  }

  // Gruppenstatistiken abrufen
  async getGroupStatistics() {
    try {
      await delay(200);

      const response = await apiClient.get('/groups');
      
      if (response) {
        const groups = response;
        
        const totalGroups = groups.length;
        const activeGroups = groups.filter(g => g.status === 'Aktiv').length;
        const totalParticipants = groups.reduce((sum, g) => sum + (g.currentParticipants || 0), 0);
        
        const groupsByCategory = {};
        const groupsByAgeGroup = {};

        groups.forEach(group => {
          // Gruppierung nach Kategorie
          const categoryName = group.category ? group.category.name : 'Unbekannt';
          if (!groupsByCategory[categoryName]) {
            groupsByCategory[categoryName] = 0;
          }
          groupsByCategory[categoryName]++;

          // Gruppierung nach Altersgruppe
          const ageGroupName = group.ageGroup ? group.ageGroup.name : 'Unbekannt';
          if (!groupsByAgeGroup[ageGroupName]) {
            groupsByAgeGroup[ageGroupName] = 0;
          }
          groupsByAgeGroup[ageGroupName]++;
        });

        return {
          success: true,
          data: {
            totalGroups,
            activeGroups,
            totalParticipants,
            groupsByCategory,
            groupsByAgeGroup
          },
          message: 'Statistiken erfolgreich geladen'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Laden der Statistiken'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Fehler beim Laden der Statistiken',
        details: error.message
      };
    }
  }

  // Mock-Daten für Fallback
  async getMockGroups() {
    await delay(300);
    
    return {
      success: true,
      data: [
        {
          id: 1,
          name: "Tan Tui - Jugend - Männer",
          description: "Wettkampfgruppe für männliche Teilnehmer in der Kategorie Tan Tui der Altersgruppe Jugend",
          category: { name: "Tan Tui", type: "Handform" },
          ageGroup: { name: "Jugend" },
          gender: "männlich",
          status: "Aktiv",
          venue: "Halle 1",
          currentParticipants: 8,
          startTime: "2026-07-15T09:00:00Z",
          endTime: "2026-07-15T11:00:00Z"
        },
        {
          id: 2,
          name: "Ba Gua Dao - Erwachsene - Frauen",
          description: "Wettkampfgruppe für weibliche Teilnehmer in der Kategorie Ba Gua Dao der Altersgruppe Erwachsene",
          category: { name: "Ba Gua Dao", type: "Waffenform" },
          ageGroup: { name: "Erwachsene" },
          gender: "weiblich",
          status: "Ausstehend",
          venue: "Halle 2",
          currentParticipants: 5,
          startTime: "2026-07-15T14:00:00Z",
          endTime: "2026-07-15T16:00:00Z"
        }
      ],
      message: 'Mock-Gruppen geladen'
    };
  }

  // Fallback-Methode: Versuche echte API, falls fehlschlägt Mock-Daten
  async getAllGroupsWithFallback() {
    try {
      return await this.getAllGroups();
    } catch (error) {
      console.warn('API nicht verfügbar, verwende Mock-Daten:', error);
      return await this.getMockGroups();
    }
  }

  // Team-Wettbewerb Gruppe abrufen
  async getTeamCompetitionGroup() {
    try {
      await delay(200);
      const response = await apiClient.get('/groups/team-competition');
      
      // apiClient gibt direkt data zurück, nicht das gesamte Response-Objekt
      // Wenn data null ist, bedeutet das, dass keine Teams vorhanden sind (kein Fehler)
      if (response !== null && response !== undefined) {
        return {
          success: true,
          data: response,
          message: 'Team-Gruppe erfolgreich geladen'
        };
      } else {
        // Keine Teams vorhanden ist kein Fehler, sondern ein normaler Zustand
        return {
          success: true,
          data: null,
          message: 'Keine Teams vorhanden'
        };
      }
    } catch (error) {
      // Bei echten Fehlern (z.B. Netzwerkfehler) geben wir einen Fehler zurück
      return {
        success: false,
        error: error.message || 'Fehler beim Laden der Team-Gruppe',
        data: null
      };
    }
  }

  // Team-Startreihenfolge aktualisieren
  async updateTeamStartOrder(teamOrder) {
    try {
      await delay(200);
      const response = await apiClient.put('/groups/team-competition/start-order', { teamOrder });
      
      if (response) {
        return {
          success: true,
          data: response,
          message: 'Team-Startreihenfolge erfolgreich aktualisiert'
        };
      } else {
        return {
          success: false,
          error: 'Fehler beim Aktualisieren der Team-Startreihenfolge'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Fehler beim Aktualisieren der Team-Startreihenfolge'
      };
    }
  }
}

export default new GroupsService();